import axiosInstance from '../lib/axios';
import { API_CONFIG } from '../config/api.config';
import type { FileWithMeta } from '../types/document.types';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('rrms_token');
  const role = localStorage.getItem('rrms_role');
  const divisionId = localStorage.getItem('rrms_division_id');
  const departmentId = localStorage.getItem('rrms_department_id');
  const designationId = localStorage.getItem('rrms_designation_id');
  const userRaw = localStorage.getItem('rrms_user');

  let userName: string | null = null;
  if (userRaw) {
    try {
      const user = JSON.parse(userRaw) as {
        name?: string;
        kgid?: string;
        designation?: string;
        designation_name?: string;
        desig_name?: string;
      };
      if (user.name && user.name.trim().length > 0) {
        userName = user.name.trim();
      } else {
        const desig = user.designation || user.designation_name || user.desig_name;
        const parts: string[] = [];
        if (desig) parts.push(desig);
        if (user.kgid) parts.push(`KGID ${user.kgid}`);
        userName = parts.length ? parts.join(' - ') : null;
      }
    } catch {
      userName = null;
    }
  }

  const headers: Record<string, string> = {
    Accept: 'application/json, text/plain, */*',
  };
  if (token) headers.token = token;
  if (role) headers['x-role-title'] = role;
  if (divisionId) headers['x-division-id'] = divisionId;
  if (departmentId) headers['x-department-id'] = departmentId;
  if (designationId) headers['x-designation-id'] = designationId;
  if (userName) headers['x-user-name'] = userName;
  return headers;
}

function parseResponseErrorPayload(text: string): string | null {
  if (!text) return null;
  try {
    const parsed = JSON.parse(text) as { message?: string };
    return parsed?.message || null;
  } catch {
    return text || null;
  }
}

function getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;
  const utf8Match = /filename\*\s*=\s*UTF-8''([^;\n]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
  if (!match) return null;
  let name = match[1].replace(/['"]/g, '').trim();
  try {
    return decodeURIComponent(name);
  } catch {
    return name || null;
  }
}

async function parseBlobErrorMessage(blob: Blob): Promise<string | null> {
  try {
    const text = await blob.text();
    const json = JSON.parse(text) as { message?: string };
    return json?.message || null;
  } catch {
    return null;
  }
}

async function toReadableError(
  err: unknown,
  fallbackMessage: string
): Promise<Error & { status?: number }> {
  const anyErr = err as any;
  const status = anyErr?.response?.status as number | undefined;
  let message =
    anyErr?.response?.data?.message || anyErr?.message || fallbackMessage;

  const data = anyErr?.response?.data;
  if (data instanceof Blob) {
    const parsed = await parseBlobErrorMessage(data);
    if (parsed) message = parsed;
  }

  const wrapped = new Error(message) as Error & { status?: number };
  wrapped.status = status;
  return wrapped;
}

export const documentService = {
  uploadDocuments: (
    caseId: string,
    filesWithMeta: FileWithMeta[],
    onUploadProgress?: (progress: number) => void
  ) => {
    const formData = new FormData();
    filesWithMeta.forEach((f) => {
      formData.append('files', f.file);
      // Legacy fallback for older backend handlers that read form fields directly.
      formData.append('folder_type', f.folder_type);
      formData.append('document_type', f.document_type);
      formData.append('classification', f.classification);
      formData.append('hashtags', JSON.stringify(f.hashtags ?? []));
    });
    formData.append(
      'metadata',
      JSON.stringify(
        filesWithMeta.map((f) => ({
          folder_type: f.folder_type,
          document_type: f.document_type,
          classification: f.classification,
          hashtags: f.hashtags,
        }))
      )
    );

    return axiosInstance.post(`/cases/${caseId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: API_CONFIG.UPLOAD_TIMEOUT,
      onUploadProgress: (e) => {
        if (onUploadProgress && e.total) {
          onUploadProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    });
  },

  getUploadStatus: (params: {
    status?: 'Pending' | 'Approved' | 'Denied';
    approval_status?: 'Pending' | 'Approved' | 'Rejected';
    q?: string;
    state?: string;
    district?: string;
    ps_name?: string;
    fir_number?: string;
    case_type?: string;
    year?: number;
    case_status?: string;
    // New record-model filters (Section/Sub-Section, File Category, Subject).
    node_id?: number;
    category_node_id?: number;
    subject?: string;
    classification?: string;
    folder_type?: string;
    document_type?: string;
    file_extension?: string;
    hashtags?: string;
    page?: number;
    limit?: number;
  }) => axiosInstance.get('/upload-status', { params }),

  getFileUrl: (documentId: string) =>
    `${API_CONFIG.BASE_URL}/documents/${documentId}/file`,

  getDownloadUrl: (documentId: string) =>
    `${API_CONFIG.BASE_URL}/documents/${documentId}/file?download=true`,

  async getFileForPreview(documentId: string): Promise<{ url: string; mimeType: string }> {
    try {
      const url = new URL(`${API_CONFIG.BASE_URL}/api/rrms/documents/${documentId}/file`);
      url.searchParams.set('_ts', String(Date.now()));
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const text = await res.text();
        const parsed = parseResponseErrorPayload(text);
        const wrapped = new Error(parsed || 'Failed to load preview') as Error & {
          status?: number;
        };
        wrapped.status = res.status;
        throw wrapped;
      }
      const blob = await res.blob();
      if (blob.type === 'application/json') {
        const parsed = await parseBlobErrorMessage(blob);
        const wrapped = new Error(parsed || 'Failed to load preview') as Error & {
          status?: number;
        };
        wrapped.status = 400;
        throw wrapped;
      }
      return {
        url: URL.createObjectURL(blob),
        mimeType: blob.type || 'application/octet-stream',
      };
    } catch (err) {
      throw await toReadableError(err, 'Failed to load preview');
    }
  },

  /**
   * Download document using authenticated request (sends token in headers).
   * Use this instead of opening getDownloadUrl() in a new tab, which does not send the token.
   */
  async downloadDocument(documentId: string, suggestedFilename?: string): Promise<void> {
    try {
      const requestUrl = new URL(`${API_CONFIG.BASE_URL}/api/rrms/documents/${documentId}/file`);
      requestUrl.searchParams.set('download', 'true');
      requestUrl.searchParams.set('_ts', String(Date.now()));
      const res = await fetch(requestUrl.toString(), {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const text = await res.text();
        const parsed = parseResponseErrorPayload(text);
        const wrapped = new Error(parsed || 'Download failed') as Error & {
          status?: number;
        };
        wrapped.status = res.status;
        throw wrapped;
      }
      const blob = await res.blob();
      if (blob.type === 'application/json') {
        const parsed = await parseBlobErrorMessage(blob);
        const wrapped = new Error(parsed || 'Download failed') as Error & {
          status?: number;
        };
        wrapped.status = 400;
        throw wrapped;
      }
      const disposition = res.headers.get('content-disposition');
      // `||` on purpose: an empty name would make the browser save the blob
      // under its random URL id, with no extension.
      const filename =
        getFilenameFromContentDisposition(disposition) ||
        suggestedFilename ||
        `document-${documentId}`;
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.rel = 'noopener noreferrer';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke later to avoid cutting off large-file downloads in some browsers.
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      throw await toReadableError(err, 'Download failed');
    }
  },

  addFavorite: (documentId: string) =>
    axiosInstance.post(`/documents/${documentId}/favorite`),

  removeFavorite: (documentId: string) =>
    axiosInstance.delete(`/documents/${documentId}/favorite`),

  updateMetadata: (
    documentId: string,
    payload: { hashtags?: string[]; classification?: 'Public' | 'Confidential' | string }
  ) => axiosInstance.patch(`/documents/${documentId}/metadata`, payload),

  /** Removes a rejected document from the uploader's active list (uploader only; server retains the file). */
  deleteRejectedDocument: (documentId: string) =>
    axiosInstance.delete(`/documents/${documentId}`),
};

export default documentService;

